'use client'

import { useState, useContext, useCallback, useEffect } from 'react'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/shadcn/tabs'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Loader2, Plus, Search } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { TeamContext } from '@/contexts/TeamContext'
import axios from '@/lib/api/axios'
import { useRouter } from 'next/navigation'
import ConfirmModal from '@/components/common/modals/ConfirmModal'
import CreateTeamModal from '@/app/manager/settings/teams/components/CreateTeamModal'
import TeamsTab from '@/app/manager/settings/teams/components/TeamsTab'

export default function Teams() {
  const authContext = useContext(AuthContext)
  const teamContext = useContext(TeamContext)
  const router = useRouter()

  const [tab, setTab] = useState('teams')
  const [, setLoading] = useState(true)
  const [initialLoaded, setInitialLoaded] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [teams, setTeams] = useState([])
  const [archivedTeams, setArchivedTeams] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [archiveTeam, setArchiveTeam] = useState(null)
  const [archiving, setArchiving] = useState(false)
  const [unarchiveTeam, setUnarchiveTeam] = useState(null)
  const [unarchiving, setUnarchiving] = useState(false)
  const [deleteTeam, setDeleteTeam] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const canCreateTeams = authContext.user?.isAuthorized('createTeams')
  const canManageTeams = authContext.user?.isAuthorized('manageTeams')

  const fetchTeams = useCallback(
    async (archived = false) => {
      try {
        const params = new URLSearchParams()
        params.append('archived', archived.toString())
        if (searchTerm) {
          params.append('search', searchTerm)
        }
        params.append('sort', 'createdAt')
        params.append('order', 'desc')

        const { data } = await axios(
          `/api/admin/organization/${authContext.user.organization.id}/teams?${params.toString()}`,
          { method: 'GET' },
          authContext,
        )

        if (archived) {
          setArchivedTeams(data.teams || [])
        } else {
          setTeams(data.teams || [])
        }
      } catch (error) {
        console.error('Failed to fetch teams:', error)
        if (archived) {
          setArchivedTeams([])
        } else {
          setTeams([])
        }
      }
    },
    [authContext, searchTerm],
  )

  useEffect(() => {
    const loadTeams = async () => {
      setLoading(true)
      await Promise.all([fetchTeams(false), fetchTeams(true)])
      setLoading(false)
      setInitialLoaded(true)
    }

    loadTeams()
  }, [fetchTeams])

  const handleArchiveTeam = useCallback(async () => {
    if (!archiveTeam) return
    setArchiving(true)

    try {
      await axios(
        `/api/admin/organization/${authContext.user.organization.id}/teams/${archiveTeam.id}/archive`,
        { method: 'POST' },
        authContext,
      )

      await Promise.all([fetchTeams(false), fetchTeams(true)])
      await teamContext.refreshTeams()

      if (teamContext.activeTeamId === archiveTeam.id) {
        teamContext.setActiveTeam(null)
      }

      setArchiveTeam(null)
    } catch (error) {
      console.error('Failed to archive team:', error)
    } finally {
      setArchiving(false)
    }
  }, [archiveTeam, authContext, teamContext, fetchTeams])

  const handleUnarchiveTeam = useCallback(async () => {
    if (!unarchiveTeam) return
    setUnarchiving(true)

    try {
      await axios(
        `/api/admin/organization/${authContext.user.organization.id}/teams/${unarchiveTeam.id}/unarchive`,
        { method: 'POST' },
        authContext,
      )

      await Promise.all([fetchTeams(false), fetchTeams(true)])
      await teamContext.refreshTeams()

      setUnarchiveTeam(null)
    } catch (error) {
      console.error('Failed to unarchive team:', error)
    } finally {
      setUnarchiving(false)
    }
  }, [unarchiveTeam, authContext, teamContext, fetchTeams])

  const handleDeleteTeam = useCallback(async () => {
    if (!deleteTeam) return
    setDeleting(true)

    try {
      await axios(
        `/api/admin/organization/${authContext.user.organization.id}/teams/${deleteTeam.id}`,
        { method: 'DELETE' },
        authContext,
      )

      await fetchTeams(true)
      await teamContext.refreshTeams()

      setDeleteTeam(null)
    } catch (error) {
      console.error('Failed to delete team:', error)
    } finally {
      setDeleting(false)
    }
  }, [deleteTeam, authContext, teamContext, fetchTeams])

  const handleTeamCreated = useCallback(() => {
    fetchTeams(false)
    teamContext.refreshTeams()
  }, [fetchTeams, teamContext])

  return (
    <>
      <div className="flex items-center mb-4">
        <h3 className="text-lg font-semibold flex-1">Teams</h3>
        {canCreateTeams && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="size-4 mr-1" />
            Create Team
          </Button>
        )}
      </div>
      <div className="flex-1 flex flex-col">
        {!initialLoaded ? (
          <div className="vertically-centered">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : (
          <Tabs
            value={tab}
            onValueChange={setTab}
            className="flex flex-col flex-1"
          >
            <TabsList>
              <TabsTrigger value="teams">Teams ({teams.length})</TabsTrigger>
              <TabsTrigger value="archive">
                Archive ({archivedTeams.length})
              </TabsTrigger>
            </TabsList>

            <div className="my-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            <TabsContent value="teams">
              <TeamsTab
                teams={teams}
                setArchiveTeam={setArchiveTeam}
                setShowCreate={setShowCreate}
                router={router}
                canEdit={canManageTeams}
                canDelete={canManageTeams}
                archived={false}
              />
            </TabsContent>

            <TabsContent value="archive">
              <TeamsTab
                teams={archivedTeams}
                setUnarchiveTeam={setUnarchiveTeam}
                setDeleteTeam={setDeleteTeam}
                router={router}
                canEdit={canManageTeams}
                canDelete={canManageTeams}
                archived={true}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <CreateTeamModal
        open={showCreate}
        setOpen={setShowCreate}
        onSuccess={handleTeamCreated}
      />

      <ConfirmModal
        open={!!archiveTeam}
        setOpen={v => !v && setArchiveTeam(null)}
        title="Archive Team"
        description={`Are you sure you want to archive "${archiveTeam?.name}"? Archived teams will be hidden from normal views and their resources will become inaccessible.`}
        acceptButtonText="Archive"
        loading={archiving}
        onAccept={handleArchiveTeam}
      />

      <ConfirmModal
        open={!!unarchiveTeam}
        setOpen={v => !v && setUnarchiveTeam(null)}
        title="Unarchive Team"
        description={`Are you sure you want to unarchive "${unarchiveTeam?.name}"? This will restore the team and make its resources accessible again.`}
        acceptButtonText="Unarchive"
        loading={unarchiving}
        onAccept={handleUnarchiveTeam}
      />

      <ConfirmModal
        open={!!deleteTeam}
        setOpen={v => !v && setDeleteTeam(null)}
        title="Delete Team Permanently"
        description={`Are you sure you want to permanently delete "${deleteTeam?.name}"? This action cannot be undone.`}
        acceptButtonText="Delete Permanently"
        loading={deleting}
        onAccept={handleDeleteTeam}
      />
    </>
  )
}
